import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useLocation } from "react-router";
import { getJson, loadSession, type Session } from "../api";
import { formatText, useTexts } from "../texts";
import { BheliLogo } from "../ui/brand";
import { ChatIcon } from "../ui/icons/Icon";
import { ToastScope } from "../ui/kit";
import { Sheet as SharedSheet } from "../ui/Sheet";
import styles from "./AdminShell.module.css";
import { personHue } from "../orgs/organizations";
import { sharedQuery } from "../hub/hub";
import { accountArea, areas, placeOf, switchOrgHref, type Area, type Place } from "./navigation";
import { OpenOrgContext, readRecentOrgs, rememberOrg, type OpenOrg, type OrgSignal } from "./openOrg";
import { useRailExpansion } from "./railExpansion";

// Marco del Admin (especificación §2, D1): menú lateral con las áreas (C47; se expande y se contrae solo, como el del
// Company Portal: railExpansion.ts), barra del módulo con la marca, el título,
// las secciones del área y la cuenta (C48), el selector de empresa en el título (C49) y, en el celular, la barra de 56 px
// con sus hojas (C27). Lo que se ve depende solo de la dirección (navigation.ts) y de lo que el servidor dijo de la sesión.

const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]!.toUpperCase())
    .join("") || "?";

/** Cierra la sesión en Identity (POST /logout con el token) y vuelve al ingreso; si Identity no responde, lo dice. */
async function signOut(): Promise<boolean> {
  const session = await loadSession().catch(() => null);
  if (!session) return false;
  const response = await fetch("/logout", {
    method: "POST",
    credentials: "same-origin",
    redirect: "manual",
    headers: { "X-CSRF-TOKEN": session.antiforgeryToken },
  });
  // El servidor responde con una redirección (opaqueredirect) solo cuando Identity revocó la sesión.
  if (response.type === "opaqueredirect" || response.ok) {
    window.location.assign("/login?error=closed");
    return true;
  }
  return false;
}

/** Esc y clic fuera cierran lo abierto; con Esc, el foco vuelve a quien lo abrió (C11). */
function useDismiss(
  open: boolean,
  close: () => void,
  container: React.RefObject<HTMLElement | null>,
  opener?: React.RefObject<HTMLElement | null>,
) {
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      close();
      opener?.current?.focus();
    };
    const onDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (container.current?.contains(target) || opener?.current?.contains(target)) return;
      close();
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDown);
    };
  }, [open, close, container, opener]);
}

export function AdminShell({ session, children }: { session: Session; children: ReactNode }) {
  const { L } = useTexts();
  const location = useLocation();
  const here = location.pathname + location.search;
  const [missingAt, setMissingAt] = useState<string | null>(null);
  // Una empresa que no existe: la barra queda como el directorio (título «Empresas», sin secciones).
  const exact = placeOf(location.pathname, location.search);
  // AR4 · un área sin permiso (Error Hub por URL) no dibuja sus secciones: la barra lleva solo su título.
  const blocked = exact.area === "hub" && !session.canErrorHub;
  const place =
    (exact.orgId && missingAt === here) || blocked ? { ...exact, orgId: null, section: null, sections: [] } : exact;
  const userId = session.userId ?? 0;
  const name = session.displayName?.trim() || session.email || L("NAV_ACCOUNT");
  const rail = useRef<HTMLElement>(null);
  const railOpener = useRef<HTMLButtonElement>(null);
  const { expanded: railOpen, toggle: toggleRail } = useRailExpansion(rail, railOpener, here);
  const [openOrg, setOpenOrgState] = useState<OpenOrg | null>(null);
  const [recents, setRecents] = useState<OpenOrg[]>(() => readRecentOrgs(userId));
  const [sheetState, setSheetState] = useState<{ kind: "areas" | "sections" | "account"; at: string } | null>(null);
  const sheet = sheetState?.at === here ? sheetState.kind : null;
  const setSheet = (kind: "areas" | "sections" | "account" | null) => setSheetState(kind ? { kind, at: here } : null);
  const visibleAreas = useMemo(() => areas.filter((area) => area.key !== "hub" || session.canErrorHub), [session]);
  // En el celular con barra fija la página desplaza dentro de `main`, que queda entre pantallas (C27, admin.css): otra
  // pantalla empieza arriba, como con el scroll de la ventana.
  const mainRef = useRef<HTMLElement>(null);
  useEffect(() => {
    if (mainRef.current) mainRef.current.scrollTop = 0;
  }, [location.pathname]);

  const setOpenOrg = useCallback(
    (org: OrgSignal) => {
      if (org === "missing") {
        setMissingAt(window.location.pathname + window.location.search);
        return;
      }
      setOpenOrgState(org);
      if (org) setRecents(rememberOrg(userId, org));
    },
    [userId],
  );

  const area = [...areas, accountArea].find((item) => item.key === place.area) ?? null;
  const org = place.orgId && openOrg?.id === place.orgId ? openOrg : null;
  const section = place.sections.find((item) => item.key === place.section) ?? null;
  // Error Hub (R8): período, entorno, módulo y versión de la dirección viajan entre las cinco secciones (§5.10).
  const sectionHref = (item: Place["sections"][number]) =>
    place.area === "hub"
      ? `${item.href(place.orgId)}${sharedQuery(new URLSearchParams(location.search))}`
      : item.href(place.orgId);

  return (
    <OpenOrgContext.Provider value={setOpenOrg}>
      <div className={`${styles.shell} tm`}>
        <ToastScope>
          <a className={styles.skip} href="#main">
            {L("NPA_SKIP")}
          </a>
          <header className={styles.bar} data-part="barra">
            <button
              ref={railOpener}
              type="button"
              className={`${styles.iconButton} ${styles.burger}`}
              aria-label={L(railOpen ? "ADM_RAIL_COLLAPSE" : "ADM_RAIL_EXPAND")}
              title={L(railOpen ? "ADM_RAIL_COLLAPSE" : "ADM_RAIL_EXPAND")}
              aria-expanded={railOpen}
              aria-controls="admin-areas"
              onClick={toggleRail}
            >
              <ChatIcon name="menu" />
            </button>
            <button
              type="button"
              className={`${styles.iconButton} ${styles.burgerMobile}`}
              aria-label={L("ADM_OPEN_MENU")}
              aria-haspopup="dialog"
              onClick={() => setSheet("areas")}
            >
              <ChatIcon name="menu" />
            </button>
            <a className={styles.brand} href="/" aria-label={L("ADM_BRAND_HOME")} data-part="marca">
              <BheliLogo variant="isotipo" size={24} theme="auto" />
              <span className={styles.brandName}>Bheli</span>
              <span className={styles.product}>{L("ADM_PRODUCT")}</span>
            </a>
            <span className={styles.separator} aria-hidden="true" />
            <ModuleTitle place={place} area={area} org={org} recents={recents} />
            {place.sections.length > 0 ? (
              <nav className={styles.sections} aria-label={L("ADM_NAV_SECTIONS")}>
                {place.sections.map((item) => (
                  <a
                    key={item.key}
                    className={styles.section}
                    href={sectionHref(item)}
                    aria-current={item.key === place.section ? "page" : undefined}
                  >
                    <ChatIcon name={item.icon} />
                    <span>{L(item.labelKey)}</span>
                  </a>
                ))}
              </nav>
            ) : null}
            <MobileTitle
              place={place}
              area={area}
              sectionLabel={section ? L(section.labelKey) : area ? L(area.labelKey) : L("ADM_PRODUCT")}
              onOpen={() => setSheet("sections")}
            />
            <AccountMenu session={session} name={name} onMobileOpen={() => setSheet("account")} />
          </header>
          <div className={styles.body}>
            {/* El hueco del riel (72 px) queda fijo; el panel se ensancha a 220 px encima del contenido, sin empujarlo. */}
            <nav
              ref={rail}
              id="admin-areas"
              className={styles.rail}
              aria-label={L("ADM_NAV_AREAS")}
              data-part="menu-lateral"
              data-expanded={railOpen ? "true" : "false"}
            >
              <div className={styles.railPanel} data-part="panel">
                {visibleAreas.map((item) => (
                  <a
                    key={item.key}
                    className={styles.railItem}
                    href={item.href}
                    aria-current={item.key === place.area ? "page" : undefined}
                    aria-label={L(item.labelKey)}
                  >
                    <ChatIcon name={item.icon} />
                    <span>{L(item.labelKey)}</span>
                  </a>
                ))}
              </div>
            </nav>
            <main id="main" ref={mainRef} tabIndex={-1} className={styles.main}>
              {children}
            </main>
          </div>
          {sheet === "areas" ? (
            <Sheet label={L("ADM_NAV_AREAS")} onClose={() => setSheet(null)}>
              <div className={styles.sheetHead}>
                <strong>Bheli {L("ADM_PRODUCT")}</strong>
              </div>
              {[...visibleAreas, accountArea].map((item) => (
                <a
                  key={item.key}
                  className={styles.sheetItem}
                  href={item.href}
                  aria-current={item.key === place.area ? "page" : undefined}
                >
                  <ChatIcon name={item.icon} />
                  <span>{L(item.labelKey)}</span>
                </a>
              ))}
            </Sheet>
          ) : null}
          {sheet === "sections" && place.sections.length > 0 ? (
            <Sheet label={L("ADM_NAV_SECTIONS")} onClose={() => setSheet(null)}>
              <div className={styles.sheetHead}>
                <strong title={org?.name}>
                  {place.orgId ? (org?.name ?? L("ADM_COMPANY")) : area ? L(area.labelKey) : ""}
                </strong>
                {place.orgId ? (
                  <a className="tm-btn secondary" href="/Organizations/Index" data-part="cambiar">
                    {L("ADM_ORG_CHANGE")}
                  </a>
                ) : null}
              </div>
              {place.sections.map((item) => (
                <a
                  key={item.key}
                  className={styles.sheetItem}
                  href={sectionHref(item)}
                  aria-current={item.key === place.section ? "page" : undefined}
                >
                  <ChatIcon name={item.icon} />
                  <span>{L(item.labelKey)}</span>
                </a>
              ))}
            </Sheet>
          ) : null}
          {sheet === "account" ? (
            <Sheet label={L("NAV_ACCOUNT")} onClose={() => setSheet(null)}>
              <AccountItems session={session} name={name} />
            </Sheet>
          ) : null}
        </ToastScope>
      </div>
    </OpenOrgContext.Provider>
  );
}

/** Título del módulo: el área con su ícono; con una empresa abierta, su nombre con el selector (C49). */
function ModuleTitle({
  place,
  area,
  org,
  recents,
}: {
  place: Place;
  area: Area | null;
  org: OpenOrg | null;
  recents: OpenOrg[];
}) {
  const { L } = useTexts();
  if (place.orgId) return <OrgSelector place={place} org={org} recents={recents} />;
  if (!area) return null;
  return (
    <span className={styles.title} data-part="titulo">
      <ChatIcon name={area.icon} className={styles.titleIcon} />
      <span className={styles.titleText}>{L(area.labelKey)}</span>
    </span>
  );
}

/** C49 · nombre de la empresa abierta con chevron: buscar, «Recientes» (4, ✓ en la actual) y «Ver todas las empresas». */
function OrgSelector({ place, org, recents }: { place: Place; org: OpenOrg | null; recents: OpenOrg[] }) {
  const { L } = useTexts();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [found, setFound] = useState<{ query: string; items: OpenOrg[]; failed: boolean } | null>(null);
  const menu = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const close = useCallback(() => {
    setOpen(false);
    setQuery("");
    setFound(null);
  }, []);
  useDismiss(open, close, menu, trigger);

  useEffect(() => {
    if (open) requestAnimationFrame(() => input.current?.focus());
  }, [open]);

  // C06: la búsqueda sale al servidor 50 ms después de la última tecla; la última respuesta gana.
  useEffect(() => {
    const term = query.trim();
    if (!term) return;
    let live = true;
    const timer = window.setTimeout(() => {
      void getJson<{ items: { organizationId: number; code: string; name: string }[] }>(
        `/api/admin/organizations/page?search=${encodeURIComponent(term)}&pageSize=8`,
      ).then((reply) => {
        if (!live) return;
        setFound({
          query: term,
          failed: reply.status !== 200,
          items: (reply.body?.items ?? []).map((item) => ({
            id: item.organizationId,
            name: item.name,
            code: item.code,
          })),
        });
      });
    }, 50);
    return () => {
      live = false;
      window.clearTimeout(timer);
    };
  }, [query]);

  const list = query.trim() ? (found?.items ?? []) : recents;
  const href = (id: number) => switchOrgHref(location.pathname, location.search, id);
  return (
    <div className={styles.selector}>
      <button
        ref={trigger}
        type="button"
        className={`${styles.title} ${styles.titleButton}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        title={org?.name}
        data-part="titulo"
        onClick={() => (open ? close() : setOpen(true))}
      >
        <ChatIcon name="building-2" className={styles.titleIcon} />
        {org ? (
          <span className={styles.titleText}>{org.name}</span>
        ) : (
          <span className={styles.titleSkeleton} aria-label={L("ADM_COMPANY")} />
        )}
        <ChatIcon name="chevron-down" className={styles.titleChevron} />
      </button>
      {open ? (
        <div ref={menu} className={styles.orgMenu} role="dialog" aria-label={L("ADM_ORG_SWITCH")} data-part="menu">
          <label className={styles.orgSearch}>
            <ChatIcon name="search" />
            <input
              ref={input}
              type="search"
              value={query}
              placeholder={L("ADM_ORG_SEARCH")}
              aria-label={L("ADM_ORG_SEARCH")}
              onChange={(event) => setQuery(event.target.value)}
            />
          </label>
          {!query.trim() && recents.length > 0 ? <span className={styles.menuLabel}>{L("ADM_ORG_RECENT")}</span> : null}
          {query.trim() && found?.failed ? (
            <p className={styles.menuNote} role="alert">
              {L("ADM_ORG_SEARCH_FAILED")}
            </p>
          ) : null}
          {query.trim() && found && !found.failed && list.length === 0 ? (
            <p className={styles.menuNote} role="status">
              {formatText(L("ADM_ORG_NO_MATCH"), found.query)}
            </p>
          ) : null}
          {list.map((item) => (
            <a key={item.id} className={styles.menuOption} href={href(item.id)} onClick={close}>
              <span className={styles.menuOptionName}>{item.name}</span>
              {/* C49 · el código distingue nombres repetidos; ✓ en la actual. */}
              <span className={styles.menuOptionCode}>{item.code}</span>
              {item.id === place.orgId ? (
                <ChatIcon name="check" className={styles.menuCheck} />
              ) : (
                <span className={styles.menuCheckSpace} aria-hidden="true" />
              )}
            </a>
          ))}
          <div className={styles.menuSeparator} />
          <a className={styles.menuOption} href="/Organizations/Index" onClick={close} data-part="ver-todas">
            {L("ADM_ORG_ALL")}
          </a>
        </div>
      ) : null}
    </div>
  );
}

/** Celular: «{sección} ▾» abre la hoja con las secciones del área; sin secciones, solo el título. */
function MobileTitle({
  place,
  area,
  sectionLabel,
  onOpen,
}: {
  place: Place;
  area: Area | null;
  sectionLabel: string;
  onOpen: () => void;
}) {
  const { L } = useTexts();
  if (place.sections.length === 0)
    return <span className={styles.mobileTitle}>{area ? L(area.labelKey) : sectionLabel}</span>;
  return (
    <button type="button" className={styles.mobileTitle} aria-haspopup="dialog" onClick={onOpen} data-part="seccion">
      <span>{sectionLabel}</span>
      <ChatIcon name="chevron-down" />
    </button>
  );
}

/** C11 · la cuenta: nombre, correo y rol; Mi perfil, Preferencias, Seguridad y Cerrar sesión. */
function AccountMenu({ session, name, onMobileOpen }: { session: Session; name: string; onMobileOpen: () => void }) {
  const { L } = useTexts();
  const [open, setOpen] = useState(false);
  const menu = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const close = useCallback(() => setOpen(false), []);
  useDismiss(open, close, menu, trigger);
  return (
    <div className={styles.account} data-part="cuenta">
      <button
        ref={trigger}
        type="button"
        className={styles.accountTrigger}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`${L("NAV_ACCOUNT")}: ${name}`}
        onClick={() => (window.matchMedia("(max-width: 767px)").matches ? onMobileOpen() : setOpen((value) => !value))}
      >
        <span
          className={`${styles.avatar} npa-person-tone`}
          style={{ ["--person-hue" as string]: String(personHue(session.userId || name)) }}
          aria-hidden="true"
        >
          {initials(name)}
        </span>
        <span className={styles.accountName}>{name}</span>
        <ChatIcon name="chevron-down" className={styles.accountChevron} />
      </button>
      {open ? (
        <div ref={menu} className={styles.accountMenu} role="menu" aria-label={L("NAV_ACCOUNT")}>
          <AccountItems session={session} name={name} />
        </div>
      ) : null}
    </div>
  );
}

function AccountItems({ session, name }: { session: Session; name: string }) {
  const { L } = useTexts();
  const [failed, setFailed] = useState(false);
  const role =
    session.role === "PLATFORM_ADMIN"
      ? L("PLATFORM_ROLE_ADMIN")
      : session.role === "PLATFORM_SELLER"
        ? L("PLATFORM_ROLE_SELLER")
        : null;
  return (
    <>
      <div className={styles.identity}>
        <strong>{name}</strong>
        {session.email ? <span>{session.email}</span> : null}
        {role ? <span className={styles.role}>{role}</span> : null}
      </div>
      <a className={styles.menuItem} role="menuitem" href="/Account/Profile">
        <ChatIcon name="user" />
        <span>{L("ACCOUNT_MY_PROFILE")}</span>
      </a>
      <a className={styles.menuItem} role="menuitem" href="/Account/Preferences">
        <ChatIcon name="sliders-horizontal" />
        <span>{L("ACCOUNT_PREFERENCES")}</span>
      </a>
      <a className={styles.menuItem} role="menuitem" href="/Account/Security">
        <ChatIcon name="shield-check" />
        <span>{L("ACCOUNT_SECURITY")}</span>
      </a>
      <div className={styles.menuSeparator} />
      <button
        type="button"
        className={styles.menuItem}
        role="menuitem"
        onClick={() => void signOut().then((done) => setFailed(!done))}
      >
        <ChatIcon name="log-out" />
        <span>{L("ACCOUNT_SIGN_OUT")}</span>
      </button>
      {failed ? (
        <p className={styles.menuNote} role="alert">
          {L("NPA_SIGN_OUT_FAILED")}
        </p>
      ) : null}
    </>
  );
}

/** C27 1.14.9 · las hojas del marco (áreas, secciones, cuenta) son la hoja compartida (RC1 · K1). */
function Sheet({ label, onClose, children }: { label: string; onClose: () => void; children: ReactNode }) {
  return (
    <SharedSheet label={label} onClose={onClose}>
      {children}
    </SharedSheet>
  );
}
